import { useQuery } from "@tanstack/react-query";
import { get, post } from "./divertidogsApiUtils";

export const QUERY_VENTAS_POR_PAGAR = "QUERY_VENTAS_POR_PAGAR";

export const useObtenerVentasPorPagar = () => {
  return useQuery(
    [QUERY_VENTAS_POR_PAGAR],
    async () => await obtenerVentasPorPagar()
  );
};

export type ConceptoVenta = ConceptoVentaServicio | "COMPRA";

export type ConceptoVentaServicio =
  | "SERVICIO_GUARDERIA"
  | "SERVICIO_HOTEL"
  | "SERVICIO_SPA"
  | "SERVICIO_ENTRENAMIENTO"
  | "SERVICIO_TRANSPORTE";

export const conceptoEsDeServicio = (
  concepto: ConceptoVenta
): concepto is ConceptoVentaServicio => {
  return [
    "SERVICIO_GUARDERIA",
    "SERVICIO_HOTEL",
    "SERVICIO_SPA",
    "SERVICIO_ENTRENAMIENTO",
    "SERVICIO_TRANSPORTE",
  ].includes(concepto);
};

export interface VentaResumida {
  id: string;
  mascota: string;
  nombresPropietario: string;
  fecha: string;
  total: number;
  pendiente: number;
}

export interface DetalleVentaPorPagar {
  id: string;
  descripcion: string;
  cantidad: number;
  valor: number;
  subTotal: number;
}

export type MetodoPago =
  | "EFECTIVO"
  | "NEQUI"
  | "TRANSFERENCIA"
  | "DAVIPLATA"
  | "TARJETA_CREDITO";

export type TipoServicio =
  | "GUARDERIA"
  | "ENTRENAMIENTO"
  | "HOTEL"
  | "SPA"
  | "TRANSPORTE";

export interface AbonoVentaPorPagar {
  id: string;
  fecha: Date;
  monto: number;
  metodo: MetodoPago;
}

export const obtenerVentasPorPagar = async (): Promise<VentaResumida[]> => {
  let resp = await get<VentaResumida[] | undefined>(`ventas/por_pagar`);
  return resp || [];
};

export interface Pago {
  idVenta: string;
  monto: number;
  fecha: Date;
  metodo: MetodoPago;
}

export interface PagoRequest {
  monto: number;
  fecha: Date;
  metodo: MetodoPago;
}

export const pagar = async (pago: Pago) => {
  const request: PagoRequest = {
    monto: pago.monto,
    fecha: pago.fecha,
    metodo: pago.metodo,
  };
  await post<PagoRequest, unknown>(`ventas/${pago.idVenta}/pagar`, request);
};

export interface VentasDelMes {
  total: number;
  totalPendiente: number;
  ventas: VentaDelMes[];
}

export interface VentaDelMes {
  id: string;
  fecha: Date;
  mascota: string;
  nombresPropietario: string;
  total: number;
  pendiente: number;
  pagada: boolean;
  conceptosVenta: ConceptoVenta[];
}

export type VentasPorMes = Record<number, VentasDelMes>;

export type VentasPorAno = Record<number, VentasPorMes>;

export const obtenerVentasPorAno = async (): Promise<VentasPorAno> => {
  let resp = await get<VentasPorAno | undefined>(`ventas/por_ano`);
  if (resp) {
    Object.keys(resp).forEach((anoStr) => {
      const ano = parseInt(anoStr);
      Object.keys(resp![ano]).forEach((mesStr) => {
        const mes = parseInt(mesStr);
        resp![ano][mes].ventas = resp![ano][mes].ventas.map((venta) => ({
          ...venta,
          fecha: new Date(venta.fecha),
        }));
      });
    });
    return resp;
  } else {
    return {};
  }
};

export const QUERY_VENTAS_POR_ANO = "QUERY_VENTAS_POR_ANO";

export const useObtenerVentasPorAno = () => {
  return useQuery(
    [QUERY_VENTAS_POR_ANO],
    async () => await obtenerVentasPorAno()
  );
};

export const QUERY_TOTAL_FACTURADO_DEL_MES = "QUERY_TOTAL_FACTURADO_DEL_MES";

export const useObtenerTotalFacturacionDelMes = () =>
  useQuery(
    [QUERY_TOTAL_FACTURADO_DEL_MES],
    async () => await obtenerTotalFacturacionDelMes()
  );

export const obtenerTotalFacturacionDelMes = async () => {
  return get<number>(`ventas/total_facturado_del_mes`);
};

export const QUERY_TOTAL_PAGOS_DEL_MES = "QUERY_TOTAL_PAGOS_DEL_MES";

export const useObtenerTotalPagosDelMes = () =>
  useQuery(
    [QUERY_TOTAL_PAGOS_DEL_MES],
    async () => await obtenerTotalPagosDelMes()
  );

export const obtenerTotalPagosDelMes = async () => {
  return get<number>(`ventas/total_pagos_del_mes`);
};

export const QUERY_BUSCAR_VENTA = "QUERY_BUSCAR_VENTA";

export const useBuscarVenta = (id: string) =>
  useQuery([QUERY_BUSCAR_VENTA], async () => await buscarVenta(id));

export const buscarVenta = async (id: string) => {
  return get<Venta>(`ventas/${id}`);
};

export interface Venta {
  id: string;
  nombresPropietario: string;
  fecha: string;
  total: number;
  pendiente: number;
  detalles: DetalleVentaPorPagar[];
  pagos: PagoVenta[];
  devoluciones: DevolucionVenta[];
  notasCredito: NotaCredito[];
}

export interface DetalleVenta {
  id: string;
  descripcion: string;
  cantidad: number;
  valor: number;
}

export interface PagoVenta {
  id: string;
  fecha: string;
  monto: number;
  metodo: MetodoPago;
}

export interface DevolucionVenta {
  id: string;
  fecha: string;
  descripcion: string | undefined;
  monto: number;
  metodo: MetodoPago;
}

export interface NotaCredito {
  id: string;
  fecha: string;
  descripcion: string | undefined;
  monto: number;
}
